import React from "react";
import { Box, Stack, Select, MenuItem } from "@mui/material";
import { tokens, motion } from "../theme";
import { pressable } from "./pressableProps";
import { pageRange, pageItems } from "./pageRange";

/**
 * The footer under a long table: what you are looking at, how much of it, and
 * how to move.
 *
 * The book puts this under the muster, the payroll register and the asset
 * register in the same shape — "Showing 1–25 of 142 people · Rows 25 ▾ ·
 * Previous 1 2 3 Next" — and it had been written inline, once, on the muster.
 * A register showing all 142 rows with no footer is the other failure: the
 * reader cannot tell whether they are seeing everything.
 */
const numberSx = (active) => ({
  minWidth: "1.75rem",
  height: "1.75rem",
  px: "0.375rem",
  borderRadius: "6px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "0.8125rem",
  fontWeight: active ? 600 : 400,
  fontVariantNumeric: "tabular-nums",
  cursor: "pointer",
  color: active ? tokens.card : tokens.textMuted,
  background: active ? tokens.primary : "transparent",
  border: `1px solid ${active ? tokens.primary : "transparent"}`,
  transition: `background ${motion.duration[1]}ms ${motion.easing.standard}`,
  "&:hover": { background: active ? tokens.primary : tokens.surfaceSubtle },
});

const stepSx = (disabled) => ({
  px: "0.75rem",
  height: "1.75rem",
  display: "flex",
  alignItems: "center",
  borderRadius: "6px",
  fontSize: "0.8125rem",
  border: `1px solid ${tokens.borderStrong}`,
  color: disabled ? tokens.textPlaceholder : tokens.textMuted,
  cursor: disabled ? "default" : "pointer",
  // A disabled step is stated, not hidden: a control that disappears at the
  // edges makes the row jump as you page.
  opacity: disabled ? 0.55 : 1,
  "&:hover": disabled ? {} : { background: tokens.surfaceSubtle },
});

const TablePager = ({
  total = 0,
  page = 1,
  rows = 25,
  onPage,
  onRows,
  rowOptions = [25, 50, 100],
  /** What is being counted — "people", "assets", "claims". */
  noun = "rows",
  nounSingular,
}) => {
  const { first, last, pages, page: current } = pageRange(page, total, rows);
  const single = nounSingular || String(noun).replace(/s$/, "");

  return (
    <Stack
      direction={{ xs: "column", sm: "row" }}
      alignItems={{ sm: "center" }}
      justifyContent="space-between"
      spacing={1.5}
      sx={{ mt: "1.125rem" }}
    >
      <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted, fontVariantNumeric: "tabular-nums" }}>
        {total === 0
          ? `No ${noun} to show`
          : `Showing ${first}–${last} of ${total} ${total === 1 ? single : noun}`}
      </Box>

      <Stack direction="row" alignItems="center" spacing={1.5}>
        {onRows && (
          <Stack direction="row" alignItems="center" spacing={0.75}>
            <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted }}>Rows</Box>
            <Select
              size="small"
              value={rows}
              onChange={(e) => onRows(Number(e.target.value))}
              inputProps={{ "aria-label": "Rows per page" }}
              sx={{
                fontSize: "0.8125rem",
                height: "1.75rem",
                background: tokens.surface,
                "& .MuiOutlinedInput-notchedOutline": { borderColor: tokens.borderStrong },
              }}
            >
              {rowOptions.map((n) => (
                <MenuItem key={n} value={n} sx={{ fontSize: "0.8125rem" }}>{n}</MenuItem>
              ))}
            </Select>
          </Stack>
        )}

        {pages > 1 && (
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Box
              {...(current > 1 ? pressable(() => onPage(current - 1)) : {})}
              aria-disabled={current === 1}
              sx={stepSx(current === 1)}
            >
              Previous
            </Box>
            {pageItems(current, pages).map((p, i) =>
              p === "…" ? (
                <Box key={`gap-${i}`} sx={{ px: "0.25rem", color: tokens.textPlaceholder, fontSize: "0.8125rem" }}>
                  …
                </Box>
              ) : (
                <Box key={p} {...pressable(() => onPage(p))} sx={numberSx(p === current)}>
                  {p}
                </Box>
              )
            )}
            <Box
              {...(current < pages ? pressable(() => onPage(current + 1)) : {})}
              aria-disabled={current === pages}
              sx={stepSx(current === pages)}
            >
              Next
            </Box>
          </Stack>
        )}
      </Stack>
    </Stack>
  );
};

export default TablePager;
